import { Component, type ErrorInfo, type ReactNode } from "react";
import { withTranslation, type WithTranslation } from "react-i18next";
import WidgetShell from "@/components/widgets/WidgetShell";

interface WidgetErrorBoundaryProps extends WithTranslation {
    /** Already-translated tile label, reused so the failed tile keeps its identity. */
    label: string;
    /** Stable, non-translated widget name used for the console diagnostic. */
    name: string;
    children: ReactNode;
}

interface WidgetErrorBoundaryState {
    hasError: boolean;
}

/**
 * Widget-scoped sibling of the root {@link ErrorBoundary} in
 * `src/components/ErrorBoundary.tsx`. The root boundary replaces the WHOLE page
 * with a full-screen error screen, which is the wrong blast radius for the
 * decorative tiles in the Widgets section: a bad lookup inside one toy must not
 * take down the rest of the site. This variant degrades a single tile to a
 * translated "could not load" message inside the usual {@link WidgetShell}
 * chrome, and stops the error before it reaches the root boundary.
 */
class WidgetErrorBoundaryBase extends Component<WidgetErrorBoundaryProps, WidgetErrorBoundaryState> {
    constructor(props: WidgetErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): WidgetErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error(`Widget crashed: ${this.props.name}`, error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            const { t, label } = this.props;
            return (
                <WidgetShell label={label} status={t("widgets.error.status")}>
                    <p className="text-sm text-theme-light opacity-80">
                        {t("widgets.error.message")}
                    </p>
                </WidgetShell>
            );
        }

        return this.props.children;
    }
}

export const WidgetErrorBoundary = withTranslation()(WidgetErrorBoundaryBase);
export default WidgetErrorBoundary;
